<script lang="ts">
    /**
     * FeaturesSection - Section 3: 주요 기능 소개
     *
     * 메인 10개 카드 (2열x5행) + 서브 3개 (접기/펼치기)
     */

    let showMore = $state(false);
    let previewImage = $state<string | null>(null);

    // 2026-10-03 앱 화면(크림 테마). 증상·약물·소셜은 새 화면이 없어 아이콘 카드로 둔다 — 옛 초록 화면과 섞지 않는다.
    const screenshotBase = '/app-guide/screenshot/android/Screenshot_20261003_';

    const mainFeatures = [
        { icon: '\uD83C\uDFAF', name: '근거리 협동 챌린지', desc: '친구와 함께하는 모험적 건강 챌린지', screenshot: `${screenshotBase}152137.png` },
        { icon: '\uD83C\uDF10', name: '온라인 챌린지', desc: '전국 사용자와 함께하는 건강 미션', screenshot: `${screenshotBase}152121.png` },
        { icon: '\uD83C\uDFCB\uFE0F', name: '운동 기록 & 활동 분석', desc: '스마트워치 연동 자동 운동 기록', screenshot: `${screenshotBase}152016.png` },
        { icon: '\u2764\uFE0F', name: '바이탈 사인 모니터링', desc: '혈압, 심박수, 체중을 한눈에', screenshot: `${screenshotBase}151945.png` },
        { icon: '\uD83D\uDC8A', name: '증상 & 약물 관리', desc: '증상 기록부터 복약 알림까지' },
        { icon: '\uD83E\uDD57', name: '영양 관리', desc: '식사 기록, 영양 분석, 수분 섭취 추적', screenshot: `${screenshotBase}152035.png` },
        { icon: '\uD83E\uDD16', name: 'AI 건강 분석', desc: '나만을 위한 AI 맞춤 건강 인사이트 (8종)', screenshot: `${screenshotBase}152043.png` },
        { icon: '\uD83C\uDFAE', name: '게이미피케이션', desc: '레벨업하며 건강해지는 재미' },
        { icon: '\uD83D\uDC65', name: '소셜 커뮤니티', desc: '함께 건강해지는 커뮤니티' },
        { icon: '\uD83C\uDF99\uFE0F', name: '음성 어시스턴트', desc: '말로 기록하는 스마트 헬스케어' },
    ];

    const subFeatures = [
        { icon: '\uD83D\uDCAC', name: 'CS 챗봇', desc: 'Gemini AI 기반 24시간 고객 지원' },
        { icon: '\uD83D\uDCCA', name: '건강 데이터 공유', desc: '내 건강 데이터를 카드로 SNS 공유' },
        { icon: '\uD83D\uDED2', name: '상점 & 크레딧', desc: '구독 플랜, 크레딧 구매, 포인트 교환' },
    ];
</script>

<section id="features" class="features-section">
    <div class="features-inner">
        <h2 class="features-title">건강의 모든 것, 한 앱에서</h2>
        <p class="features-subtitle">
            운동, 영양, 건강관리, AI 분석까지 — 13가지 기능으로 당신의 건강을 360&deg; 케어합니다.
        </p>

        <!-- Main Cards Grid -->
        <div class="features-grid">
            {#each mainFeatures as feature}
                <div
                    class="feature-card"
                    class:has-screenshot={feature.screenshot}
                    onmouseenter={() => { if (feature.screenshot) previewImage = feature.screenshot; }}
                    onmouseleave={() => { previewImage = null; }}
                >
                    <span class="feature-icon">{feature.icon}</span>
                    <div class="feature-info">
                        <h4 class="feature-name">{feature.name}</h4>
                        <p class="feature-desc">{feature.desc}</p>
                    </div>
                    {#if feature.screenshot}
                        <div class="feature-screenshot-thumb">
                            <img src={feature.screenshot} alt="{feature.name} 화면" loading="lazy" />
                        </div>
                    {/if}
                </div>
            {/each}
        </div>

        <!-- Screenshot Preview Modal -->
        {#if previewImage}
            <div class="screenshot-preview" onclick={() => previewImage = null}>
                <div class="preview-phone">
                    <img src={previewImage} alt="앱 화면 미리보기" />
                </div>
            </div>
        {/if}

        <!-- More Features Toggle -->
        <div class="more-section">
            <button class="more-toggle" onclick={() => showMore = !showMore}>
                {showMore ? '접기' : '더 많은 기능'}
                <svg
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="more-arrow"
                    class:rotated={showMore}
                >
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>

            {#if showMore}
                <div class="sub-features">
                    {#each subFeatures as feature}
                        <div class="feature-card sub">
                            <span class="feature-icon">{feature.icon}</span>
                            <div class="feature-info">
                                <h4 class="feature-name">{feature.name}</h4>
                                <p class="feature-desc">{feature.desc}</p>
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
    </div>
</section>

<style>
    .features-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg-alt, #F8F9FA);
    }

    .features-inner {
        max-width: 64rem;
        margin: 0 auto;
    }

    .features-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        text-align: center;
        color: var(--landing-text, #1A1A1A);
        margin: 0 0 0.75rem;
    }

    .features-subtitle {
        font-size: 1rem;
        color: var(--landing-text-sub, #666666);
        text-align: center;
        margin: 0 0 3rem;
        line-height: 1.6;
    }

    .features-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
    }

    .feature-card {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        background: #fff;
        border: 1px solid rgba(30, 92, 58, 0.08);
        border-radius: 14px;
        padding: 1.25rem;
        transition: all 0.2s;
    }

    .feature-card:hover {
        border-color: rgba(30, 92, 58, 0.2);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
        transform: translateY(-1px);
    }

    .feature-card.has-screenshot {
        cursor: pointer;
    }

    .feature-icon {
        font-size: 1.75rem;
        flex-shrink: 0;
    }

    .feature-info {
        flex: 1;
    }

    .feature-name {
        font-size: 1rem;
        font-weight: 600;
        color: var(--landing-text, #1A1A1A);
        margin: 0 0 0.25rem;
    }

    .feature-desc {
        font-size: 0.85rem;
        color: var(--landing-text-sub, #666666);
        margin: 0;
        line-height: 1.5;
    }

    /* Screenshot Thumbnail */
    .feature-screenshot-thumb {
        flex-shrink: 0;
        width: 48px;
        height: 48px;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid rgba(30, 92, 58, 0.1);
        opacity: 0.8;
        transition: opacity 0.2s;
    }

    .feature-card:hover .feature-screenshot-thumb {
        opacity: 1;
    }

    .feature-screenshot-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
    }

    /* Screenshot Preview (hover popup) */
    .screenshot-preview {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        cursor: pointer;
        animation: fadeIn 0.2s ease;
    }

    .preview-phone {
        width: 280px;
        max-height: 80vh;
        background: #1a1a1a;
        border-radius: 32px;
        padding: 12px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    }

    .preview-phone img {
        width: 100%;
        height: auto;
        border-radius: 22px;
        display: block;
    }

    /* More Toggle */
    .more-section {
        text-align: center;
    }

    .more-toggle {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.6rem 1.5rem;
        background: transparent;
        border: 1px solid rgba(30, 92, 58, 0.15);
        border-radius: 100px;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--landing-primary, #1E5C3A);
        cursor: pointer;
        transition: all 0.2s;
    }

    .more-toggle:hover {
        background: rgba(30, 92, 58, 0.06);
    }

    .more-arrow {
        transition: transform 0.3s;
    }

    .more-arrow.rotated {
        transform: rotate(180deg);
    }

    .sub-features {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
        margin-top: 1.5rem;
        animation: fadeIn 0.3s ease;
    }

    .feature-card.sub {
        background: rgba(255, 255, 255, 0.7);
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @media (min-width: 768px) {
        .features-section {
            padding: 6rem 2.5rem;
        }

        .features-grid {
            grid-template-columns: 1fr 1fr;
        }

        .sub-features {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .features-section {
            padding: 7rem 3rem;
        }
    }
</style>
